import * as Yup from 'yup';
import * as React from 'react';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';

import {
	CerebroText,
	CerebroDrawer,
	CerebroHeading,
	CerebroBreadcrumb,
	CerebroPrimaryButton,
	CerebroPageContainer,
	CerebroForm,
	CerebroStyledBox,
	CerebroLabel,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroYesNo,
} from '../shared/components';
import { useApi } from '../shared/context';
import { Deal, DealFinancialMetric, RunwayLeftInMonths, RunwayLeftInMonthsMap } from '../shared/models';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { QueryKeys, useDealQuery, useNavigateToNextPage } from '../shared/hooks';
import { useDealFinancialMetricsQuery } from '../shared/hooks/Queries/useDealFinancialMetricsQuery';

import {
	IncomeStatementRow,
	IncomeStatementFormValues,
	buildFormValues,
	parseFormValuesToPatches,
	IncomeStatementMetricTypes,
	DefaultIncomeStateFormValues,
	adjustEbitda,
} from '../shared/components/IncomeStatementRow';

import { ReactComponent as General2 } from '../shared/icons/General2.svg';
import { IncomeStatementHeadings } from '../shared/components/IncomeStatementHeadings';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { getApiResponseData } from '../shared/utils/axios';
import { AxiosInstance } from 'axios';

export const AcquisitionIncomeStatement: React.FC = () => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const [isEbitdaExplainerOpen, setIsEbitdaExplainerOpen] = React.useState(false);
	const [isEbitdaAdjustmentsExplainerOpen, setIsEbitdaAdjustmentsExplainerOpen] = React.useState(false);

	const dealQuery = useDealQuery(guid);
	const dealFinancialMetricsQuery = useDealFinancialMetricsQuery(guid, 'Acquisition', IncomeStatementMetricTypes);

	const { mutate } = useMutation(
		async ({ values }: MutationVariables) => {
			let deal: Deal | null = null;
			const metrics = await getAndUpdateDealFinancialMetrics(api, guid, dealFinancialMetricsQuery.data, values);

			const ebitda = NumberUtils.ParseDecimal(values['Ebitda|trailingTwelveMonths'] ?? '0');
			const adjustments = NumberUtils.ParseDecimal(values['Adjustments|trailingTwelveMonths'] ?? '0');
			const adjustedEbitda = (ebitda ?? 0) + (adjustments ?? 0);

			const { data, error } = await getApiResponseData<Deal>(
				api.patch(CerebroApiEndpoints.Deals.Root(guid), {
					acquisitionRunwayLeftInMonths:
						adjustedEbitda >= 0 ? 'CashFlowPositive' : values.acquisitionRunwayLeftInMonths,
					acquisitionHaveRecurringRevenue: values.acquisitionHaveRecurringRevenue,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			deal = data;

			return { deal, metrics };
		},

		{
			onSuccess: ({ metrics, deal }) => {
				client.setQueryData(QueryKeys.Deal.Root(guid), deal);
				client.setQueryData(QueryKeys.FinancialMetrics.All(guid, 'Acquisition'), metrics);

				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<>
			<CerebroPageContainer maxWidth={'100%'}>
				<CerebroBreadcrumb>Financials &gt; Acquisition &gt; Income Statement</CerebroBreadcrumb>

				<CerebroHeading gutterBottom>And about the acquisition target&#39;s finances...</CerebroHeading>
				<CerebroText sx={{ marginBottom: 6 }}>
					Finding the right acquisition loan and best options requires accurate information about the target
					company finances.
				</CerebroText>

				<Formik
					validationSchema={IncomeStatementSchema}
					enableReinitialize
					initialValues={{
						acquisitionRunwayLeftInMonths: dealQuery.data?.acquisitionRunwayLeftInMonths ?? 'NotSelected',
						acquisitionHaveRecurringRevenue: dealQuery.data?.acquisitionHaveRecurringRevenue ?? null,
						...(buildFormValues(dealFinancialMetricsQuery.data) ?? DefaultIncomeStateFormValues),
					}}
					onSubmit={FormikUtils.MutateOnSubmit(mutate)}
				>
					{({ values, isSubmitting, handleSubmit, errors, touched }) => {
						return (
							<CerebroForm onSubmit={handleSubmit}>
								<CerebroHeading size="h2" gutterBottom>
									P&#38;L / Cashflow Details
								</CerebroHeading>

								<IncomeStatementHeadings />

								<Grid container alignItems="center" spacing={2} sx={{ marginBottom: 6 }}>
									<IncomeStatementRow
										formValues={values}
										isLoading={dealFinancialMetricsQuery.isLoading}
										isSubmitting={isSubmitting}
										metricType="TotalRevenue"
									/>
									<IncomeStatementRow
										formValues={values}
										isLoading={dealFinancialMetricsQuery.isLoading}
										isSubmitting={isSubmitting}
										metricType="NetIncome"
									/>
									<IncomeStatementRow
										formValues={values}
										isLoading={dealFinancialMetricsQuery.isLoading}
										isSubmitting={isSubmitting}
										metricType="Ebitda"
										onInfoClick={() => setIsEbitdaExplainerOpen(true)}
									/>

									<Grid item xs={12}>
										<hr />
									</Grid>

									<IncomeStatementRow
										formValues={values}
										isLoading={dealFinancialMetricsQuery.isLoading}
										isSubmitting={isSubmitting}
										metricType="Adjustments"
										onInfoClick={() => setIsEbitdaAdjustmentsExplainerOpen(true)}
									/>

									<IncomeStatementRow
										formValues={values}
										isLoading={dealFinancialMetricsQuery.isLoading}
										isSubmitting={isSubmitting}
										metricType="AdjustedEbitda"
									/>
								</Grid>

								{adjustEbitda(values, 'trailingTwelveMonths') < 0 && (
									<>
										<CerebroLabel
											required
											htmlFor="acquisitionRunwayLeftInMonths"
											error={FormikUtils.IsInvalid(
												'acquisitionRunwayLeftInMonths',
												errors,
												touched
											)}
										>
											What is the cash runway?
										</CerebroLabel>
										<Field
											id="acquisitionRunwayLeftInMonths"
											placeholder="Amount in months"
											as={CerebroDropdown}
											name="acquisitionRunwayLeftInMonths"
										>
											{Object.entries(RunwayLeftInMonthsMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</>
								)}

								<CerebroLabel htmlFor="acquisitionHaveRecurringRevenue" sx={{ marginTop: 6 }}>
									Does the company have recurring revenue?
								</CerebroLabel>
								<Field
									id="acquisitionHaveRecurringRevenue"
									as={CerebroYesNo}
									name="acquisitionHaveRecurringRevenue"
									disabled={isSubmitting}
								/>
								{values.acquisitionHaveRecurringRevenue && (
									<Grid container alignItems="center" spacing={2} sx={{ marginBottom: 6 }}>
										<IncomeStatementRow
											formValues={values}
											isLoading={dealFinancialMetricsQuery.isLoading}
											isSubmitting={isSubmitting}
											metricType="Mrr"
										/>
									</Grid>
								)}

								<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
									Save &amp; Continue
								</CerebroPrimaryButton>
							</CerebroForm>
						);
					}}
				</Formik>
			</CerebroPageContainer>

			<CerebroDrawer
				icon={General2}
				open={isEbitdaExplainerOpen}
				onClose={() => setIsEbitdaExplainerOpen(false)}
				PaperProps={{ sx: { maxWidth: { md: 600 } } }}
			>
				<CerebroStyledBox>
					<CerebroText sx={{ marginBottom: 3 }}>
						EBITDA – EBITDA = Net Income + Taxes + Interest Expense + Depreciation &amp; Amortization.
					</CerebroText>
					<CerebroText>
						EBITDA Addbacks – Please add back any extraordinary impacts to EBITDA. The purpose of this is to
						get a normalized view of EBITDA. Extraordinary impacts may include severance pay, one time
						expenses, one time gains, owners comp (if the owner is being bought out), etc.
					</CerebroText>
				</CerebroStyledBox>
			</CerebroDrawer>

			<CerebroDrawer
				icon={General2}
				open={isEbitdaAdjustmentsExplainerOpen}
				onClose={() => setIsEbitdaAdjustmentsExplainerOpen(false)}
				PaperProps={{ sx: { maxWidth: { md: 600 } } }}
			>
				<CerebroStyledBox>
					<CerebroText>EBITDA Adjustments Include Owners Compensation, Synergies, Etc.</CerebroText>
				</CerebroStyledBox>
			</CerebroDrawer>
		</>
	);
};

const IncomeStatementSchema = Yup.object().shape({
	acquisitionRunwayLeftInMonths: Yup.string()
		.notOneOf(['-1'])
		.required('Please indicate number of months left in runway'),
	acquisitionHaveRecurringRevenue: Yup.boolean(),
});

type MutationVariables = FormikUtils.MutateOnSubmitVariables<
	IncomeStatementFormValues & {
		acquisitionRunwayLeftInMonths: RunwayLeftInMonths;
		acquisitionHaveRecurringRevenue: boolean | null;
	}
>;

async function getAndUpdateDealFinancialMetrics(
	api: AxiosInstance,
	guid: string | undefined,
	metrics: DealFinancialMetric[] | undefined,
	formValues: IncomeStatementFormValues
): Promise<DealFinancialMetric[]> {
	const { data, error } = await getApiResponseData<DealFinancialMetric[]>(
		api.patch(CerebroApiEndpoints.Metrics.Root(guid), parseFormValuesToPatches('Acquisition', metrics, formValues))
	);

	if (error) {
		throw new Error(error.response?.data);
	}

	return data ?? ([] as DealFinancialMetric[]);
}
